// designLoader.js — Design Studio: ngarkon dizajnin AKTIV nga baza e të dhënave
// dhe e zbaton në të gjithë aplikacionin si variabla CSS (`--bg`, `--accent`…),
// me përditësim LIVE kur platform admini publikon një dizajn të ri.
//
// Përdoret NGA TË DY app-et (aplikacioni kryesor dhe Admin Center) — thjesht
// importo `supabase` nga klienti përkatës.
//
// PËRDORIMI (te main.jsx, PARA render-it të parë):
//   import { initDesign } from "./designLoader";
//   await initDesign();            // zbaton dizajnin aktiv + dëgjon ndryshimet
//   ReactDOM.createRoot(...).render(<App/>);
//
// Ndërrimi light/dark nga përdoruesi:
//   import { setMode, getDesign } from "./designLoader";
//   setMode("dark");               // ose "light"
//
// Kodi ekzistues që lexon COLORS/THEME (objekte JS + stile inline) mund të
// urëzohet me `paletteForMode()` — shih INTEGRIMI.md.

import { supabase } from "./supabaseClient";

// Fallback i sigurt (Moderne — e ndritshme) nëse DB s'përgjigjet.
// REZERVA — neutrale me qëllim, dhe që nga 20 gushti me `bgDeep`.
//
// Mungesa e tij ishte defekti i fundit i "pulsimit të kuq": `--bgDeep` vendosej
// vetëm kur zbatohej një temë e vërtetë dizajni, ndaj çdo rregull i shkruar si
// `var(--bgDeep, <ngjyra e COLORS>)` binte te rezerva e vet — dhe `COLORS` para
// ngarkimit është tema klasike, e kuqja. Pra vetë "rregullimi" im e pikturonte
// atë që donte të shmangte. Tani variabla ekziston gjithmonë dhe është neutrale.
const FALLBACK = {
  meta: { family: "both" },
  appearance: { defaultMode: "light", allowUserToggle: true },
  fonts: { display: "'Fraunces', Georgia, serif", body: "'Inter', system-ui, sans-serif" },
  scale: { radius: 16, shadow: "0 1px 2px rgba(36,31,24,.05),0 6px 20px rgba(36,31,24,.08)", shadowLg: "0 20px 50px rgba(36,31,24,.22)" },
  light: { bg: "#faf8f5", bgDeep: "#efe9e0", surface: "#ffffff", surface2: "#f4efe8", border: "#eae3d8", text: "#241f18", text2: "#6f6555", muted: "#9c9384", accent: "#3f6f52", accentSoft: "#e6f0e9", accentBorder: "#bcd8c8", bronze: "#b0813f", bronzeSoft: "#f5ecdd" },
  dark: { bg: "#17140f", bgDeep: "#100e0a", surface: "#211d16", surface2: "#2a251c", border: "#332d22", text: "#efe8db", text2: "#b6ac99", muted: "#847a68", accent: "#6fae86", accentSoft: "#1f2f26", accentBorder: "#2f4a3a", bronze: "#d3a45f", bronzeSoft: "#2f2716" },
  branch: { core: "#4C7A4A", married_in: "#c96f8a", cousin: "#3E7EA6", extended: "#C99A28", divorced: "#C0392B" },
  layout: { navVariant: "classic" },
};

const MODE_KEY = "pef_design_mode";
const CACHE_KEY = "pef_design_cache"; // dizajni i fundit aktiv — aplikohet sinkron në nisje (pa "flash" klasik)
let current = { tokens: FALLBACK, mode: "light", id: null, name: null, designId: null, themeKey: null, themeMode: null, crestOn: true, designs: [], themes: [] };
const listeners = new Set();
let mediaArmed = false;

/** Modaliteti i sistemit (pajisjes) — për temën "Follow system". */
function systemMode() {
  try { return (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light"; }
  catch (_e) { return "light"; }
}
/** Bashkon tokenat bazë të design-it me override-in e temës (deep-merge i seksioneve). */
function mergeTokens(base, override) {
  const b = base || {}, o = override || {};
  const out = { ...b, ...o };
  ["light", "dark", "branch", "fonts", "scale", "emblems", "appearance", "layout"].forEach((k) => {
    if (b[k] || o[k]) out[k] = { ...(b[k] || {}), ...(o[k] || {}) };
  });
  return out;
}

function toCamelSafe(v) { return v; }

function pickMode(tokens, preferred) {
  // Nëse kërkohet SHPREHIMISHT një modalitet dhe paleta e tij ekziston, përdore
  // GJITHMONË — tema (light/dark/system) e komandon modalitetin, jo
  // `allowUserToggle` i dizajnit. (FIX: "E çelët" te Classic dilte e kuqe sepse
  // appearance.allowUserToggle=false e detyronte defaultMode='dark'.)
  if (preferred && tokens?.[preferred]) return preferred;
  const def = tokens?.appearance?.defaultMode || "light";
  return tokens?.[def] ? def : (tokens?.light ? "light" : "dark");
}

/** Kthen paletën e sheshtë të modalitetit aktual (për urëzim me COLORS/THEME). */
export function paletteForMode(mode = current.mode) {
  const t = current.tokens || FALLBACK;
  return { ...(t[mode] || t.light || {}), branch: t.branch || {}, fonts: t.fonts || {}, scale: t.scale || {} };
}

export function getDesign() { return current; }
export function onDesignChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// ════════════════════════════════════════════════════════════════════════════
// SHKRONJAT E DIZAJNIT — 3 shtator 2026
//
// BOSHLLËKU QË MBULON: `tokens.fonts.display` dhe `.body` vendosnin emrin e
// familjes, po askush nuk i SHKARKONTE shkronjat. Skedarët vinin nga një
// `@import` i ngurtë te `index.css`, me pesë familje të zgjedhura muaj më parë.
// Domethënë një dizajn i ri që kërkon, të themi, Cormorant Garamond, do të
// dukej sikur punon — dhe do të binte pa zë te Georgia. Një token që emërton
// diçka që s'ekziston është pikërisht ai lloj fushe që gënjen.
//
// TANI dizajni i mban vetë shkronjat: `fonts.url`. Ngarkohen kur AI bëhet
// aktiv, jo për të gjithë përherë — pra një dizajn i pazgjedhur nuk i kushton
// asnjë kërkesë askujt.
//
// ⚠ VETËM Google Fonts. Tokenat i shkruan platform-admini, po një `<link>` te
// një origjinë e huaj është CSS i palës së tretë brenda faqes; kjo derë rri e
// ngushtë me qëllim. Nëse një ditë duhet një ofrues tjetër, shtohet KËTU, i
// emëruar — jo duke e hapur fare.
let fontetTani = null;
function ngarkoFontet(url) {
  try {
    const u = String(url || "").trim();
    if (!u || u === fontetTani) return;
    if (!/^https:\/\/fonts\.googleapis\.com\//i.test(u)) {
      console.warn("[dizajni] shkronjat u shpërfillën — vetëm fonts.googleapis.com lejohet:", u);
      return;
    }
    let el = document.getElementById("pef-design-fonts");
    if (!el) {
      el = document.createElement("link");
      el.id = "pef-design-fonts";
      el.rel = "stylesheet";
      document.head.appendChild(el);
    }
    el.href = u;
    fontetTani = u;
  } catch (_e) { /* pa DOM (SSR/test) */ }
}

/** Zbaton tokenat si variabla CSS në :root. */
export function applyDesign(tokens, mode) {
  const t = tokens || FALLBACK;
  const m = pickMode(t, mode);
  const p = t[m] || {};
  const root = document.documentElement;
  const set = (k, v) => { if (v != null) root.style.setProperty(k, String(v)); };

  Object.entries(p).forEach(([k, v]) => set("--" + k, v));             // paleta
  if (t.branch) Object.entries(t.branch).forEach(([k, v]) => set("--branch-" + k, v));
  if (t.scale) {
    if (t.scale.radius != null) set("--radius", (typeof t.scale.radius === "number" ? t.scale.radius + "px" : t.scale.radius));
    set("--shadow", t.scale.shadow);
    set("--shadow-lg", t.scale.shadowLg);
  }
  if (t.fonts) { set("--font-display", t.fonts.display); set("--font-body", t.fonts.body); ngarkoFontet(t.fonts.url); }
  root.setAttribute("data-mode", m);
  // ⚠ 4 shtator 2026 — «në dark mode, në disa fusha teksti merr ngjyrën e fushës».
  //
  // SHKAKU, dhe është një rresht që MUNGONTE, jo një rresht i gabuar.
  // Askund nuk vendosej `color-scheme`. Pa të, shfletuesi supozon «i çelët» dhe
  // i vizaton KONTROLLET E VETA — listat rënëse të `select`-it, kalendarin e
  // `input[type=date]`, sfondin e autofill-it, shiritat e rrëshqitjes — me
  // pamjen e çelët: sfond i bardhë. Kurse CSS-ja jonë i jep tekstit
  // `var(--text)`, që te tema e errët është gati i bardhë. Tekst i bardhë mbi
  // të bardhë: fusha duket bosh, ose «teksti merr ngjyrën e fushës».
  //
  // Nuk zgjidhet dot me ngjyra: ato sipërfaqe i pikturon vetë shfletuesi dhe
  // s'i prek asnjë rregull i yni. E vetmja mënyrë për t'i thënë «kjo faqe është
  // e errët» është kjo veti. Te temat e çelëta vlera është ajo që shfletuesi
  // supozonte tashmë, pra asgjë nuk ndryshon; te të errëtat, ndreqet gjithçka
  // përnjëherë — çdo fushë, çdo listë, çdo dizajn.
  try { root.style.colorScheme = m === "dark" ? "dark" : "light"; } catch (_e) { /* pa DOM */ }

  current = { ...current, tokens: t, mode: m };
  listeners.forEach((fn) => { try { fn(current); } catch (_e) {} });
}

/** Ndërro light/dark (nëse dizajni e lejon). */
export function setMode(mode) {
  try { localStorage.setItem(MODE_KEY, mode); } catch (_e) {}
  applyDesign(current.tokens, mode);
}

async function fetchAndApply() {
  let preferred;
  try { preferred = localStorage.getItem(MODE_KEY) || undefined; } catch (_e) {}
  let responded = false;
  try {
    const { data, error } = await supabase.rpc("rpc_get_active_design");
    if (!error) {
      responded = true;
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.tokens) {
        current.id = row.id; current.name = row.name; current.followSystem = false;
        applyDesign(row.tokens, preferred);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ tokens: row.tokens, id: row.id, name: row.name })); } catch (_e) {}
        return;
      }
    }
  } catch (_e) { /* pa rrjet / pa DB */ }

  if (!responded) {
    // OFFLINE — serveri s'u arrit. MOS e prek dizajnin e përzgjedhur dhe MOS e
    // fshij cache-në. Nëse kishim një dizajn të cache-uar (u aplikua sinkron te
    // initDesign) → mbaje TË PËRZGJEDHURIN. Kur kthehet interneti, `online`/
    // resolveAndApply e rifreskon. Pa cache (nisja e parë pa internet) → temë
    // NEUTRALE që ndjek sistemin (light/dark), KURRË e kuqja klasike.
    if (current.id) return;
    applyDesign(FALLBACK, systemMode());
    current = { ...current, id: null, name: null, followSystem: true };
    try { localStorage.setItem(MODE_KEY, current.mode); } catch (_e) {}
    listeners.forEach((fn) => { try { fn(current); } catch (_e2) {} });
    return;
  }

  // Serveri U PËRGJIGJ dhe s'ka dizajn aktiv → sjellja klasike: MOS pikturo
  // FALLBACK-un, lëri CSS-variablat të biejnë te pamja klasike ekzakt.
  current = { ...current, tokens: FALLBACK, id: null, name: null, followSystem: false };
  try { localStorage.removeItem(CACHE_KEY); } catch (_e) {}
  listeners.forEach((fn) => { try { fn(current); } catch (_e2) {} });
}

// ---------------------------------------------------------------------
// HIERARKIA (Faza 2): Design → Temë → (Follow system) → Crest
// Zgjidh design-in dhe temën e përdoruesit; nëse RPC-të e reja s'ekzistojnë
// (SQL-ja s'është ekzekutuar ende) → bie te modeli i vjetër (fetchAndApply).
// ---------------------------------------------------------------------
// SIGURIA2 (U2) — numëruesi i thirrjeve. `resolveAndApply` thirret nga disa vende
// (nisja, kthimi i rrjetit, hyrja, ruajtja e pamjes) dhe secila ka disa `await`;
// një thirrje e vjetër që mbaron pas një të reje do t'ia mbishkruante pamjen
// zgjedhjes më të fundit. Vetëm thirrja më e re ka të drejtë të aplikojë.
let ritjaDizajnit = 0;
async function resolveAndApply() {
  const ritja = ++ritjaDizajnit;
  let preferred; try { preferred = localStorage.getItem(MODE_KEY) || undefined; } catch (_e) {}
  try {
    let appearance = null;
    // 20 gusht 2026 — këtu ishte `const { data } = ...` brenda një `try/catch`.
    // Duket si trajtim gabimi, POR supabase-js nuk HEDH kurrë për një RPC që
    // dështon: kthen `{ data: null, error }`. Pra `catch` s'aktivizohej asnjëherë,
    // `error` s'lexohej fare, dhe `appearance` mbetej null në heshtje — çka
    // poshtë bie te design-i i parazgjedhur dhe te tema e parë. Rezultati:
    // zgjedhja e përdoruesit "harrohej" në çdo hyrje, pa asnjë shenjë pse.
    {
      const { data, error } = await supabase.rpc("rpc_get_my_appearance");
      if (error) console.warn("[pamja] rpc_get_my_appearance dështoi:", error.message || error);
      appearance = Array.isArray(data) ? data[0] : data;
    }

    let designs = [];
    try { const { data, error } = await supabase.rpc("rpc_list_designs_for_me"); if (!error) designs = data || []; } catch (_e) {}
    if (ritja !== ritjaDizajnit) return;   // U2 — një thirrje më e re e ka zëvendësuar
    if (!designs.length) { await fetchAndApply(); return; } // RPC mungon / s'ka design → modeli i vjetër

    const design = (appearance && appearance.design_id && designs.find((d) => d.id === appearance.design_id))
                || designs.find((d) => d.is_default) || designs[0];

    let themes = [];
    {
      const { data, error } = await supabase.rpc("rpc_list_themes_for_design", { p_design_id: design.id });
      if (error) console.warn("[pamja] rpc_list_themes_for_design dështoi:", error.message || error);
      themes = data || [];
    }

    // ⚠ 5 shtator 2026 — «NDIQ SISTEMIN» ZGJIDHEJ VETVETIU.
    //
    // Ky rresht ishte: e ruajtura e njeriut → E PARA E LISTËS. Hapi i mesit —
    // tema e shënuar `is_default` — MUNGONTE. PAMJA3 (2 shtator) e shtoi atë
    // kolonë te baza dhe te RPC-ja, po gjysma e klientit s'ishte këtu; njësoj
    // si rasti i «Rreth platformës», ku një skedar i vjetër e kishte mbuluar
    // punën e bërë. Dhe «Ndiq sistemin» e ka `sort_order = 0`, pra ishte
    // gjithmonë e para — prandaj fitonte pa e zgjedhur askush.
    //
    // Tani: e ruajtura → `is_default` → e para JO-`system` → e para sido që të
    // jetë. Temat `system` mbeten plotësisht të zgjedhshme; thjesht nuk
    // imponohen më.
    const joSistem = themes.filter((t) => t && t.mode !== "system");
    const theme = (appearance && appearance.theme_key && themes.find((t) => t.key === appearance.theme_key))
               || themes.find((t) => t && t.is_default)
               || joSistem[0] || themes[0] || null;

    let mode = (theme && theme.mode) || preferred || null;
    if (mode === "system") mode = systemMode();

    if (ritja !== ritjaDizajnit) return;   // U2 — pas listës së temave
    const merged = mergeTokens(design.tokens, theme && theme.tokens);
    current = {
      ...current, id: design.id, name: design.name, designId: design.id, designs, themes,
      themeKey: theme ? theme.key : null, themeMode: theme ? theme.mode : null,
      crestOn: appearance ? appearance.crest_on !== false : true,
    };
    applyDesign(merged, mode || undefined);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ tokens: merged, id: design.id, name: design.name, mode: current.mode })); } catch (_e) {}
    armMedia();
  } catch (_e) { await fetchAndApply(); }
}

function armMedia() {
  if (mediaArmed) return; mediaArmed = true;
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    // Ndjek light/dark të sistemit edhe kur tema është "system" edhe kur jemi te
    // rezerva "follow system" (offline, pa cache).
    const handler = () => { if (current.themeMode === "system" || current.followSystem) applyDesign(current.tokens, systemMode()); };
    if (mq.addEventListener) mq.addEventListener("change", handler); else if (mq.addListener) mq.addListener(handler);
  } catch (_e) {}
}

// Kthen `{ error }` — thirrësi vendos si ta tregojë. Më parë kthente void dhe
// e fshihte dështimin: ndërfaqja e ndryshonte pamjen, baza jo, dhe ndryshimi
// zhdukej me hyrjen e radhës.
async function saveAppearance(designId, themeKey, crestOn) {
  const { error } = await supabase.rpc("rpc_set_my_appearance", { p_design_id: designId, p_theme_key: themeKey, p_crest_on: crestOn });
  if (error) console.warn("[pamja] rpc_set_my_appearance dështoi:", error.message || error);
  await resolveAndApply();
  return { error: error || null };
}
/** Përdoruesi zgjedh një DESIGN (nga të lejuarat). Rivendos temën te parazgjedhja.
 *
 *  ⚠ 5 shtator 2026 — «edhe pse e kam bërë favorit Red and Black, prapë më
 *  shfaqet Ndiq sistemin kur bëj switch nga Trashëgimia te Rrënjët».
 *
 *  Shkaku: këtu dërgohet `theme_key = null`, me kuptimin «rivendose te
 *  parazgjedhja» — po funksioni te baza e trajton `null` si «mos e prek», pra
 *  çelësi i vjetër mbetet. Dhe meqë `system` ekziston te TË DY dizajnet, ai
 *  çelës gjendet edhe te dizajni i ri dhe fiton si «zgjedhje e ruajtur».
 *
 *  Prandaj rivendosja bëhet e shprehur: pas kalimit, nëse dizajni i ri ka një
 *  temë parazgjedhur dhe ajo s'është e zgjedhura, ruhet ajo. Dy thirrje në
 *  vend të njërës, po pa prekur funksionin e bazës dhe lejet e tij.
 */
export async function setUserDesign(id) {
  const res = await saveAppearance(id, null, current.crestOn !== false);
  try {
    const def = (current.themes || []).find((t) => t && t.is_default);
    if (def && current.themeKey !== def.key) {
      return await saveAppearance(id, def.key, current.crestOn !== false);
    }
  } catch (_e) { /* mbetet ajo që u zgjodh më sipër */ }
  return res;
}
/** Përdoruesi zgjedh një TEMË brenda design-it aktual. */
export async function setUserTheme(key) { return saveAppearance(current.designId || current.id, key, current.crestOn !== false); }
/** Përdoruesi ndez/fik Family Crest (i pavarur nga tema). */
export async function setUserCrest(on) { return saveAppearance(current.designId || current.id, current.themeKey, !!on); }

export function getDesigns() { return current.designs || []; }
export function getThemes() { return current.themes || []; }
export function getAppearance() { return { designId: current.designId || current.id, themeKey: current.themeKey, themeMode: current.themeMode, crestOn: current.crestOn !== false }; }
export function getCrestOn() { return current.crestOn !== false; }

/** Thirre njëherë në nisje. Zbaton dizajnin aktiv + dëgjon publikimet LIVE. */
/**
 * Zbaton zgjedhjen e RUAJTUR të vetë përdoruesit, në mënyrë sinkrone.
 *
 * Kjo thirret te FUNDI I KËTIJ MODULI, jo te `initDesign()`, dhe dallimi është
 * i gjithë çështja. Modulet ES vlerësohen para atij që i importon: pra kur
 * `App.jsx` fillon të ekzekutohet, ngjyrat janë tashmë ato të përdoruesit.
 * Më parë kjo ndodhte brenda `initDesign()`, të cilën `main.jsx` e thërret pas
 * importeve — dhe në atë hendek `App.jsx` i kishte ngulur konstantet e veta te
 * tema klasike, e kuqja. Rezultati: korniza e parë e çdo rifreskimi vizatohej
 * me një temë që përdoruesi s'e kishte zgjedhur kurrë.
 *
 * PARIMI: platforma nuk ka temë të parazgjedhur për pajisjen tënde. Ka vetëm
 * atë që ke zgjedhur ti, të ruajtur këtu. Ajo e Admin Center-it është vendimi
 * i serverit, dhe ai vjen menjëherë pas — dhe kur vjen, e mbishkruan këtë dhe
 * rishkruan cache-në. Pra e para vizaton, e dyta vendos.
 */
export function applyCachedDesign() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return false;
    const c = JSON.parse(raw);
    if (!c || !c.tokens) return false;
    current.id = c.id || null;
    current.name = c.name || null;
    applyDesign(c.tokens, c.mode || undefined);
    return true;
  } catch (_e) { return false; }
}

export async function initDesign() {
  applyCachedDesign();
  await resolveAndApply();
  armMedia();
  // PAS HYRJES DHE PAS DALJES — mungesa e kësaj ishte defekti "pas login-it
  // kthehet te default".
  //
  // `initDesign()` thirret te `main.jsx` në ngarkim të modulit, pra PARA se të
  // ketë sesion. Në atë çast `auth.uid()` është null, `rpc_get_my_appearance`
  // s'kthen asnjë rresht, dhe kodi bie te design-i i parazgjedhur me temën e
  // parë. Deri sot asnjë ngjarje s'e rizgjidhte pamjen kur përdoruesi hynte:
  // dëgjoheshin `online`, `visibilitychange` dhe publikimet e adminit — por jo
  // identifikimi. Pra gjithë sesioni kalonte me pamjen e vizitorit anonim, dhe
  // zgjedhja e ruajtur e përdoruesit nuk lexohej kurrë.
  //
  // `setTimeout(…, 0)` nuk është zbukurim: supabase-js paralajmëron shprehimisht
  // të mos thirren metoda të tjera të tij BRENDA këtij callback-u — bllokon
  // rrugën e autentikimit. E shtyjmë një cikël, jashtë tij.
  try {
    supabase.auth.onAuthStateChange((event) => {
      if (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        setTimeout(() => { resolveAndApply(); }, 0);
      }
    });
  } catch (_e) { /* pa auth — pamja mbetet ajo e parazgjedhur */ }

  // KUR KTHEHET INTERNETI → rifresko te dizajni I PËRZGJEDHUR (largo rezervën
  // "follow system"). Edhe kur aplikacioni kthehet në plan të parë (native/web).
  try {
    window.addEventListener("online", () => { resolveAndApply(); });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && (current.followSystem || navigator.onLine !== false)) resolveAndApply();
    });
  } catch (_e) { /* pa DOM — pa rëndësi */ }
  // LIVE: kur admini publikon/ndryshon dizajn ose tema, rifresko vetë.
  try {
    supabase
      .channel("design-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "design_presets" }, () => { resolveAndApply(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "design_themes" }, () => { resolveAndApply(); })
      .subscribe();
  } catch (_e) { /* realtime jo i disponueshëm — jo kritike */ }
}

export default {
  initDesign, applyDesign, setMode, getDesign, onDesignChange, paletteForMode,
  setUserDesign, setUserTheme, setUserCrest, getDesigns, getThemes, getAppearance, getCrestOn,
};

// Zbatohet sapo ngarkohet moduli — para se çdo importues të ekzekutohet.
// Shih `applyCachedDesign` më lart për arsyen.
try { applyCachedDesign(); } catch (_e) { /* pa DOM/localStorage — jo kritike */ }
